<template>
  <div
    class="progress-ring"
    :class="classes">
    <div class="progress-ring__ring" />

    <div
      v-if="showPercentage"
      class="progress-ring__percentage">
      <slot
        :percentage="clampedValue"
        :label="percentageLabel">
        {{ percentageLabel }}
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults( defineProps<{
  percentage: number;
  variant?: string;
  size?: "xs" | "sm" | "md" | "lg";
  showPercentage?: boolean;
  thickness?: string;
}>(), {
  percentage: 100,
  variant: "primary",
  size: "sm",
  showPercentage: false,
  thickness: undefined,
} );

const clampedValue = computed( () => Math.max( 0, Math.min( 100, props.percentage ) ) );
const valuePercentage = computed( () => `${clampedValue.value}%` );
const percentageLabel = computed( () => `${Math.floor( clampedValue.value )}%` );

const classes = computed( () =>
  [
    `progress-ring--${props.variant}`,
    `progress-ring--${props.size}`,
  ] );

const thick = computed( () => {
  if ( props.thickness ) {
    return props.thickness;
  }

  switch ( props.size ) {
    case "xs":
      return "5px";
    case "sm":
      return "6px";
    case "md":
      return "10px";
    case "lg":
      return "15px";
    default:
      return "10px";
  }
} );
</script>

<style scoped lang="scss">
.progress-ring {
  position: relative;
  width: var(--size);
  height: var(--size);

  &--xs {
    --size: calc(var(--s-5) * 2);
    font-size: var(--s-3);
  }

  &--sm {
    --size: calc(var(--s-6) * 2);
    font-size: var(--s-4);
  }

  &--md {
    --size: calc(var(--s-7) * 2);
    font-size: var(--s-5);
  }

  &--lg {
    --size: calc(var(--s-8) * 2);
    font-size: var(--s-6);
  }

  --thickness: v-bind(thick);
  --color: var(--theme-color, --color-primary-500);
  --color-background: var(--theme-color-background, --color-primary-200);

  &__ring {
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    position: relative;
    overflow: hidden;
    background: radial-gradient(closest-side, transparent calc(100% - var(--thickness) - 1px), transparent calc(100% - var(--thickness))),
    radial-gradient(farthest-side, var(--color) 98%, #0000) top/var(--thickness) var(--thickness) no-repeat,
    conic-gradient(var(--color) v-bind(valuePercentage), var(--color-background) 0);
    mask: radial-gradient(closest-side, #0000 calc(90% - var(--thickness)), #0000 calc(51%), #0000 calc(100% - var(--thickness) - 1px), white calc(100% - var(--thickness)));

    &::after {
      content: "";
      width: calc(var(--size) / var(--thickness) + 2px);
      height: calc(var(--size) / var(--thickness) + 2px);
      inset: calc(50% - (var(--thickness) + 1px) / 2);
      background-color: var(--color);
      position: absolute;
      border-radius: 50%;
      transform: rotate(calc(v-bind(clampedValue) * 3.6deg - 90deg)) translate(calc(var(--size) / 2 - 50%));
    }
  }

  &__percentage {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-weight: 500;
  }
}
</style>
